<template>
  <div id="adminTaxInvoices">
    <!-- ===== Filters + bulk actions ===== -->
    <b-card no-body class="mb-2">
      <div class="px-2 pt-2 pb-1 d-flex align-items-center">
        <feather-icon icon="FileTextIcon" size="20" class="text-primary mr-1" />
        <h5 class="mb-0">e-Factura tax invoices</h5>
        <small class="text-muted ml-1">— for accountant import (SAGA)</small>
      </div>

      <div class="px-2 pb-2">
        <b-row>
          <b-col md="3" class="mb-1">
            <label class="small mb-25">From</label>
            <b-form-input v-model="range.from" type="date" size="sm" />
          </b-col>
          <b-col md="3" class="mb-1">
            <label class="small mb-25">To</label>
            <b-form-input v-model="range.to" type="date" size="sm" />
          </b-col>
          <b-col md="2" class="mb-1">
            <label class="small mb-25">Account type</label>
            <b-form-select v-model="filter.accountType" :options="accountTypeOptions" size="sm" />
          </b-col>
          <b-col md="2" class="mb-1">
            <label class="small mb-25">Has XML</label>
            <b-form-select v-model="filter.hasXml" :options="hasXmlOptions" size="sm" />
          </b-col>
          <b-col md="2" class="mb-1 d-flex align-items-end">
            <b-button
              variant="primary"
              size="sm"
              block
              :disabled="loading"
              @click="fetchInvoices"
            >
              <b-spinner v-if="loading" small />
              <feather-icon v-else icon="RefreshCwIcon" size="14" class="mr-25" />
              Apply
            </b-button>
          </b-col>
        </b-row>

        <hr class="my-1" />

        <div class="d-flex flex-wrap align-items-center" style="gap: 8px;">
          <b-button
            variant="outline-success"
            size="sm"
            :disabled="!canExportBatch || exporting"
            @click="exportBatch"
          >
            <b-spinner v-if="exporting" small class="mr-50" />
            <feather-icon v-else icon="DownloadIcon" size="14" class="mr-25" />
            Export all XMLs (archive)
          </b-button>
          <span class="text-muted small">
            Pack all e-Factura XMLs in the selected range as a single archive.
            <span v-if="invoices.length">
              ({{ withXmlCount }} of {{ invoices.length }} have XML)
            </span>
          </span>
        </div>
      </div>
    </b-card>

    <!-- ===== Table ===== -->
    <b-card no-body>
      <b-table
        :items="filteredInvoices"
        :fields="fields"
        :per-page="perPage"
        :current-page="currentPage"
        responsive
        striped
        small
        hover
        sort-by="issued_at"
        :sort-desc="true"
        show-empty
        empty-text="No tax invoices in this range."
        class="mb-0"
      >
        <template #cell(full_number)="row">
          <strong>{{ formatNumber(row.item) }}</strong>
        </template>

        <template #cell(issued_at)="row">
          {{ formatDate(row.item.issued_at) }}
        </template>

        <template #cell(buyer)="row">
          <div class="d-flex align-items-center">
            <feather-icon
              :icon="row.item.buyer_account_type === 'business' ? 'BriefcaseIcon' : 'UserIcon'"
              size="14"
              class="mr-50"
              :class="row.item.buyer_account_type === 'business' ? 'text-primary' : 'text-muted'"
            />
            <span>
              <span v-if="row.item.buyer_account_type === 'business' && row.item.buyer_company_name">
                {{ row.item.buyer_company_name }}
              </span>
              <span v-else>{{ row.item.buyer_full_name || '—' }}</span>
              <small class="d-block text-muted">{{ row.item.buyer_country || '—' }}</small>
            </span>
          </div>
        </template>

        <template #cell(amount)="row">
          {{ Number(row.item.gross_amount).toFixed(2) }} {{ row.item.currency }}
        </template>

        <template #cell(vat)="row">
          <span v-if="row.item.reverse_charge" class="text-success small">
            <feather-icon icon="CheckCircleIcon" size="12" /> Reverse charge
          </span>
          <span v-else-if="Number(row.item.vat_amount) > 0">
            {{ formatRate(row.item.vat_rate) }}%
            <small class="text-muted">
              ({{ Number(row.item.vat_amount).toFixed(2) }} {{ row.item.currency }})
            </small>
          </span>
          <span v-else class="text-muted small">Export 0%</span>
        </template>

        <template #cell(vat_ron)="row">
          <span v-if="row.item.vat_amount_ron != null">
            {{ Number(row.item.vat_amount_ron).toFixed(2) }} RON
            <small v-if="row.item.fx_rate_to_ron" class="d-block text-muted">
              @ {{ Number(row.item.fx_rate_to_ron).toFixed(4) }}
            </small>
          </span>
          <span v-else class="text-muted">—</span>
        </template>

        <template #cell(actions)="row">
          <b-button
            variant="flat-primary"
            size="sm"
            class="btn-icon mr-25"
            v-b-tooltip.hover.top="row.item.pdf_path ? 'Download PDF' : 'PDF not generated'"
            :disabled="!row.item.pdf_path || downloadingPdfId === row.item.id"
            @click="downloadPdf(row.item)"
          >
            <b-spinner v-if="downloadingPdfId === row.item.id" small />
            <feather-icon v-else icon="FileTextIcon" size="14" />
          </b-button>
          <b-button
            variant="flat-success"
            size="sm"
            class="btn-icon"
            v-b-tooltip.hover.top="row.item.xml_path ? 'Download e-Factura XML' : 'XML not generated (legacy or BNR unavailable)'"
            :disabled="!row.item.xml_path || downloadingXmlId === row.item.id"
            @click="downloadXml(row.item)"
          >
            <b-spinner v-if="downloadingXmlId === row.item.id" small />
            <feather-icon v-else icon="CodeIcon" size="14" />
          </b-button>
        </template>
      </b-table>

      <div
        v-if="filteredInvoices.length"
        class="d-flex flex-wrap align-items-center justify-content-between px-2 py-1"
      >
        <div class="d-flex align-items-center text-muted small">
          <b-form-select
            v-model="perPage"
            :options="perPageOptions"
            size="sm"
            style="width: 80px"
            class="mr-1"
          />
          <span>per page — {{ filteredInvoices.length }} of {{ invoices.length }} invoice(s) in range.</span>
        </div>
        <b-pagination
          v-model="currentPage"
          :total-rows="filteredInvoices.length"
          :per-page="perPage"
          size="sm"
          class="mb-0"
        />
      </div>
    </b-card>

    <!-- ===== Fallback modal — shown only if backend has neither ZIP nor tar.gz ===== -->
    <b-modal
      v-model="fallbackModal.show"
      title="Archive backend unavailable"
      ok-only
      ok-title="Close"
      size="lg"
    >
      <p>
        The server can't bundle the XMLs into an archive (no <code>ext-zip</code>
        and no <code>PharData</code>). Use the per-invoice download buttons in the
        table, or right-click the URLs below to save each XML.
      </p>
      <b-list-group>
        <b-list-group-item
          v-for="row in fallbackModal.invoices"
          :key="row.id"
          class="d-flex justify-content-between align-items-center"
        >
          <span>
            <strong>{{ row.full_number }}</strong>
            <small class="text-muted ml-1">
              {{ row.gross_amount }} {{ row.currency }}
            </small>
          </span>
          <b-button
            variant="outline-success"
            size="sm"
            :disabled="downloadingXmlId === row.id"
            @click="downloadXml(row)"
          >
            <b-spinner v-if="downloadingXmlId === row.id" small class="mr-50" />
            <feather-icon v-else icon="DownloadIcon" size="14" class="mr-25" />
            Download XML
          </b-button>
        </b-list-group-item>
      </b-list-group>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BFormInput, BFormSelect, BButton, BSpinner,
  BTable, BModal, BListGroup, BListGroupItem, BPagination, VBTooltip,
} from "bootstrap-vue";
import Billing from "@/services/billing.service.js";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

export default {
  name: "AdminTaxInvoices",
  directives: { "b-tooltip": VBTooltip },
  components: {
    BCard, BRow, BCol, BFormInput, BFormSelect, BButton, BSpinner,
    BTable, BModal, BListGroup, BListGroupItem, BPagination, ToastificationContent,
  },
  data() {
    const today = new Date();
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const fmt = (d) => d.toISOString().slice(0, 10);
    return {
      loading: false,
      exporting: false,
      invoices: [],
      range: {
        from: fmt(monthStart),
        to: fmt(today),
      },
      filter: {
        accountType: null,
        hasXml: null,
      },
      currentPage: 1,
      perPage: 25,
      perPageOptions: [10, 25, 50, 100],
      accountTypeOptions: [
        { value: null, text: "All" },
        { value: "personal", text: "Personal" },
        { value: "business", text: "Business" },
      ],
      hasXmlOptions: [
        { value: null, text: "All" },
        { value: true, text: "With XML" },
        { value: false, text: "Without XML" },
      ],
      fields: [
        { key: "full_number", label: "Number", sortable: true },
        { key: "issued_at",   label: "Issued",  sortable: true },
        { key: "buyer",       label: "Buyer" },
        { key: "amount",      label: "Total",  sortable: true },
        { key: "vat",         label: "VAT" },
        { key: "vat_ron",     label: "VAT (RON)" },
        { key: "actions",     label: "" },
      ],
      downloadingPdfId: null,
      downloadingXmlId: null,
      fallbackModal: {
        show: false,
        invoices: [],
      },
    };
  },
  computed: {
    canExportBatch() {
      return !!(this.range.from && this.range.to) && this.withXmlCount > 0;
    },
    withXmlCount() {
      return this.invoices.filter((i) => !!i.xml_path).length;
    },
    filteredInvoices() {
      return this.invoices.filter((i) => {
        if (this.filter.accountType && i.buyer_account_type !== this.filter.accountType) return false;
        if (this.filter.hasXml === true  && !i.xml_path) return false;
        if (this.filter.hasXml === false &&  i.xml_path) return false;
        return true;
      });
    },
  },
  mounted() {
    this.fetchInvoices();
  },
  watch: {
    // Jump back to page 1 whenever the visible set changes (filters edited
    // or a new date range loaded) so the user never lands on an empty page.
    filteredInvoices() {
      this.currentPage = 1;
    },
  },
  methods: {
    formatNumber(item) {
      // Backend sends the authoritative full_number (handles both eras:
      // legacy IM-2026-0119 and SmartBill IM-1441 where number is null).
      if (item.full_number) return item.full_number;
      const n = String(item.number).padStart(4, "0");
      return `${item.series}-${item.year}-${n}`;
    },
    formatDate(iso) {
      if (!iso) return "—";
      const d = new Date(iso);
      return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" });
    },
    formatRate(rate) {
      const n = Number(rate);
      return Number.isInteger(n) ? n : n.toFixed(2).replace(/\.?0+$/, "");
    },
    toast(props) {
      this.$toast({ component: ToastificationContent, props });
    },
    async fetchInvoices() {
      this.loading = true;
      try {
        this.invoices = await Billing.adminListTaxInvoices(this.range);
      } catch (e) {
        this.toast({
          title: "Could not load invoices",
          icon: "XIcon",
          variant: "danger",
          text: e?.response?.data?.message || "Please try again.",
        });
        this.invoices = [];
      }
      this.loading = false;
    },
    triggerBlobDownload(blob, filename) {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    },
    async downloadPdf(item) {
      this.downloadingPdfId = item.id;
      try {
        const blob = await Billing.downloadPdf(item.id);
        this.triggerBlobDownload(blob, `${this.formatNumber(item)}.pdf`);
      } catch (e) {
        this.toast({
          title: "Could not download PDF",
          icon: "XIcon",
          variant: "danger",
          text: e?.response?.status === 404
            ? "PDF not yet generated for this invoice."
            : "Please try again.",
        });
      }
      this.downloadingPdfId = null;
    },
    async downloadXml(item) {
      this.downloadingXmlId = item.id;
      try {
        const blob = await Billing.downloadXml(item.id);
        // Item from list table has series/year/number; item from fallback
        // modal already has full_number prebuilt by backend.
        const filename = item.full_number && item.full_number.includes("-")
          ? `${item.full_number}.xml`
          : `${this.formatNumber(item)}.xml`;
        this.triggerBlobDownload(blob, filename);
      } catch (e) {
        this.toast({
          title: "Could not download XML",
          icon: "XIcon",
          variant: "danger",
          text: e?.response?.status === 404
            ? "XML not yet generated for this invoice."
            : "Please try again.",
        });
      }
      this.downloadingXmlId = null;
    },
    /**
     * Detect filename + content type from the response.
     * - "application/zip"  → efactura_<from>_<to>.zip
     * - "application/gzip" → efactura_<from>_<to>.tar.gz
     * - "application/json" → backend ran out of archive backends; show modal
     */
    async exportBatch() {
      if (!this.canExportBatch) return;
      this.exporting = true;
      try {
        const res = await Billing.exportTaxInvoicesXmlBatch(this.range);
        const ctype = (res.headers["content-type"] || "").toLowerCase();
        const blob  = res.data;

        if (ctype.includes("json")) {
          // Parse the blob back to JSON (axios was forced to blob mode)
          const text = await blob.text();
          let payload = {};
          try { payload = JSON.parse(text); } catch (_e) { /* ignore */ }
          this.fallbackModal.invoices = payload.invoices || [];
          this.fallbackModal.show = true;
          this.toast({
            title: "No archive backend available",
            icon: "InfoIcon",
            variant: "info",
            text: "Use the individual download links below.",
          });
          return;
        }

        const ext = ctype.includes("zip") && !ctype.includes("gzip") ? "zip" : "tar.gz";
        const filename = `efactura_${this.range.from}_${this.range.to}.${ext}`;
        this.triggerBlobDownload(blob, filename);
        this.toast({
          title: "Archive ready",
          icon: "CheckIcon",
          variant: "success",
          text: `Downloaded ${filename}`,
        });
      } catch (e) {
        this.toast({
          title: "Could not export archive",
          icon: "XIcon",
          variant: "danger",
          text: e?.response?.data?.message || "Please try again.",
        });
      }
      this.exporting = false;
    },
  },
};
</script>

<style lang="scss" scoped>
#adminTaxInvoices {
  h5 { font-size: 1rem; }
  label.small { font-size: 11px; color: #888; }
}
</style>
